<template>
  <div class="copywriting-view">
    <div class="cw-toolbar">
      <span class="cw-label">产品文案：</span>
      <button
        class="cw-generate-btn cw-generate-all"
        @click="workbench.generateAllCopywriting()"
        :disabled="anyLoading || !anyProductHasImage"
      >
        {{ anyLoading ? '生成中...' : '全部生成' }}
      </button>
      <button
        v-for="(product, idx) in workbench.products"
        :key="'gen'+product.id"
        class="cw-generate-btn"
        :disabled="workbench.copywriting.loading[idx] || !product.mainImage"
        @click="workbench.generateCopywriting(idx)"
      >
        {{ workbench.copywriting.loading[idx] ? '生成中...' : product.name }}
      </button>
    </div>

    <div class="cw-rows">
      <div v-for="(product, idx) in workbench.products" :key="product.id" class="cw-row">
        <div class="cw-row-header">
          <span class="cw-row-name">{{ product.name }}</span>
          <button
            v-if="workbench.copywriting.results[idx]?.titleZh"
            class="cw-toggle-zh"
            :class="{ active: showZh[idx] }"
            @click="showZh[idx] = !showZh[idx]"
          >
            {{ showZh[idx] ? '隐藏中文' : '显示中文' }}
          </button>
        </div>

        <div v-if="workbench.copywriting.errors[idx]" class="cw-error">{{ workbench.copywriting.errors[idx] }}</div>

        <div v-if="workbench.copywriting.loading[idx]" class="cw-loading">
          <div class="cw-spinner"></div>
          <span>AI 正在生成文案...</span>
        </div>

        <div v-else-if="workbench.copywriting.results[idx]" class="cw-result">
          <template v-if="workbench.copywriting.results[idx].title">
            <!-- 标题 -->
            <div class="cw-section">
              <h4>标题</h4>
              <div class="cw-line">
                <p class="cw-content">{{ workbench.copywriting.results[idx].title }}</p>
                <button class="cw-copy-btn" @click="copyText(workbench.copywriting.results[idx].title)">复制</button>
              </div>
              <p v-if="showZh[idx] && workbench.copywriting.results[idx].titleZh" class="cw-content cw-zh">{{ workbench.copywriting.results[idx].titleZh }}</p>
            </div>

            <!-- 五点描述 -->
            <div class="cw-section" v-if="getFeatures(idx).length">
              <h4>五点描述</h4>
              <div v-for="(bullet, i) in getFeatures(idx)" :key="i" class="cw-bullet-group">
                <div class="cw-line">
                  <span class="cw-bullet-num">{{ i + 1 }}.</span>
                  <p class="cw-bullet-text">{{ bullet }}</p>
                  <button class="cw-copy-btn sm" @click="copyText(bullet)">复制</button>
                </div>
                <p v-if="showZh[idx] && workbench.copywriting.results[idx].featuresZh?.[i]" class="cw-bullet-text cw-zh">{{ workbench.copywriting.results[idx].featuresZh[i] }}</p>
              </div>
            </div>

            <!-- 产品描述 -->
            <div class="cw-section" v-if="getDesc(idx)">
              <h4>产品描述</h4>
              <div class="cw-line">
                <p class="cw-content">{{ getDesc(idx) }}</p>
                <button class="cw-copy-btn" @click="copyText(getDesc(idx))">复制</button>
              </div>
              <p v-if="showZh[idx] && workbench.copywriting.results[idx].introductionZh" class="cw-content cw-zh">{{ workbench.copywriting.results[idx].introductionZh }}</p>
            </div>

            <!-- 关键词 -->
            <div class="cw-section" v-if="workbench.copywriting.results[idx].keywords">
              <h4>搜索关键词</h4>
              <div class="cw-keywords">
                <span v-for="(kw, i) in getKeywordsList(workbench.copywriting.results[idx].keywords)" :key="i" class="cw-keyword-tag">{{ kw }}</span>
              </div>
            </div>
          </template>

          <template v-else-if="workbench.copywriting.results[idx].rawText">
            <div class="cw-section">
              <h4>生成结果</h4>
              <p class="cw-content cw-pre">{{ workbench.copywriting.results[idx].rawText }}</p>
              <button class="cw-copy-btn" @click="copyText(workbench.copywriting.results[idx].rawText)">复制全部</button>
            </div>
          </template>
        </div>

        <div v-else class="cw-empty">
          <span>未生成，点击上方按钮生成文案</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, computed } from 'vue'
import { useWorkbenchStore } from '@/stores/useWorkbenchStore'

const workbench = useWorkbenchStore()

const showZh = reactive({ 0: false, 1: false, 2: false })

const anyLoading = computed(() => workbench.copywriting.loading.some(Boolean))
const anyProductHasImage = computed(() => workbench.products.some(p => p?.mainImage))

const getFeatures = (idx) => workbench.copywriting.results[idx]?.features || workbench.copywriting.results[idx]?.bullets || []
const getDesc = (idx) => workbench.copywriting.results[idx]?.introduction || workbench.copywriting.results[idx]?.description || ''

const getKeywordsList = (kw) => {
  if (!kw) return []
  if (typeof kw === 'string') return kw.split(',').map(s => s.trim()).filter(Boolean)
  if (Array.isArray(kw)) return kw
  return []
}

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text)
  } catch {
    const ta = document.createElement('textarea')
    ta.value = text
    document.body.appendChild(ta)
    ta.select()
    document.execCommand('copy')
    document.body.removeChild(ta)
  }
}
</script>

<style scoped>
.copywriting-view {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.cw-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.cw-label {
  font-size: 14px;
  font-weight: 500;
  color: #4e5969;
  white-space: nowrap;
}
.cw-generate-btn {
  padding: 7px 16px;
  background: #165dff;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.2s;
  white-space: nowrap;
}
.cw-generate-btn:disabled {
  background: #c9cdd4;
  cursor: not-allowed;
}
.cw-generate-btn:not(:disabled):hover {
  background: #0e4ddb;
}
.cw-generate-all {
  background: #0fc6c2;
}
.cw-generate-all:not(:disabled):hover {
  background: #0ba5a1;
}

/* 按行排列 */
.cw-rows {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.cw-row {
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e5e6eb;
  padding: 20px;
}
.cw-row-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f2f3f5;
}
.cw-row-name {
  font-size: 16px;
  font-weight: 600;
  color: #1d2129;
}
.cw-toggle-zh {
  padding: 4px 14px;
  border: 1px solid #e5e6eb;
  border-radius: 4px;
  background: #fff;
  color: #4e5969;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}
.cw-toggle-zh:hover {
  border-color: #165dff;
  color: #165dff;
}
.cw-toggle-zh.active {
  background: #f0f5ff;
  border-color: #165dff;
  color: #165dff;
}

/* 内容区 */
.cw-loading {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #4e5969;
  font-size: 13px;
  padding: 20px 0;
}
.cw-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid #e5e6eb;
  border-top-color: #165dff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
.cw-result {
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.cw-section h4 {
  font-size: 13px;
  font-weight: 600;
  color: #1d2129;
  margin: 0 0 8px 0;
}
.cw-line {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.cw-content {
  font-size: 13px;
  color: #4e5969;
  line-height: 1.6;
  margin: 0;
  flex: 1;
  white-space: pre-wrap;
}
.cw-pre {
  white-space: pre-wrap;
  word-break: break-all;
}
.cw-zh {
  color: #86909c;
  font-size: 12px;
  margin-top: 4px;
  padding: 6px 10px;
  background: #f7f8fa;
  border-radius: 4px;
  border-left: 3px solid #0fc6c2;
}
.cw-bullet-group {
  margin-bottom: 8px;
}
.cw-bullet-num {
  color: #165dff;
  font-weight: 600;
  flex-shrink: 0;
  font-size: 13px;
}
.cw-bullet-text {
  font-size: 13px;
  color: #4e5969;
  line-height: 1.5;
  margin: 0;
  flex: 1;
}
.cw-copy-btn {
  padding: 2px 8px;
  font-size: 11px;
  border: 1px solid #e5e6eb;
  border-radius: 4px;
  background: #fff;
  color: #4e5969;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s;
}
.cw-copy-btn:hover {
  border-color: #165dff;
  color: #165dff;
}
.cw-copy-btn.sm {
  padding: 1px 6px;
  font-size: 10px;
}
.cw-keywords {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.cw-keyword-tag {
  padding: 2px 8px;
  background: #f0f5ff;
  color: #165dff;
  border-radius: 4px;
  font-size: 11px;
}
.cw-error {
  color: #f53f3f;
  font-size: 12px;
  padding: 6px 10px;
  background: #fff0f0;
  border-radius: 4px;
}
.cw-empty {
  color: #c9cdd4;
  font-size: 13px;
  padding: 20px 0;
}
</style>
